import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DollarSign, TrendingDown, TrendingUp, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { setCanonical, setPageTitle, setMetaDescription, generateFAQSchema, generateBreadcrumbSchema, generateSpeakableSchema } from "@/utils/seo";
import { calculateBreakdown, formatMoney } from "@/utils/taxCalculator";
import { SponsorSidebar, InlineBanner, BottomAdBanner } from "@/components/SponsorBanners";
import FinancialDisclaimer from "@/components/FinancialDisclaimer";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SalaryInCityPage({ salary, citySlug, isTaxPage }) {
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!citySlug) return;
    axios.get(`${API}/cities/${citySlug}`).then(r => {
      setCity(r.data);
      const salK = salary / 1000;
      if (isTaxPage) {
        setCanonical(`/${salK}k-salary-after-tax-in-${citySlug}`);
        setPageTitle(`${formatMoney(salary)} Salary After Tax in ${r.data.state}`);
        setMetaDescription(`What does a ${formatMoney(salary)} salary look like after taxes in ${r.data.state}? See your take-home pay, tax breakdown, and monthly budget.`);
      } else {
        setCanonical(`/${salK}k-salary-in-${citySlug}`);
        setPageTitle(`${formatMoney(salary)} Salary in ${r.data.name}, ${r.data.state_code} - Can You Afford It?`);
        setMetaDescription(`Can you afford to live in ${r.data.name} on a ${formatMoney(salary)} salary? See your take-home pay, rent ratio, and full expense breakdown.`);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [citySlug, salary, isTaxPage]);

  if (loading) return <div className="container-main py-24 text-center text-slate-400">Loading...</div>;
  if (!city) return <div className="container-main py-24 text-center"><h1 className="text-2xl font-bold">City Not Found</h1></div>;

  const result = calculateBreakdown(salary, city);
  const salK = salary / 1000;
  const verdictClass = result.verdict === "Comfortable" ? "verdict-comfortable" : result.verdict === "Tight" ? "verdict-tight" : "verdict-not-affordable";

  const faqs = [
    { question: `How much is ${formatMoney(salary)} after taxes in ${city.name}?`, answer: `On a ${formatMoney(salary)} salary in ${city.name}, ${city.state_code}, you'd take home approximately ${formatMoney(result.net_annual)} per year or ${formatMoney(result.net_monthly)} per month after federal taxes (${formatMoney(result.federal_tax)}), state taxes (${formatMoney(result.state_tax)}), and FICA (${formatMoney(result.fica_tax)}).` },
    { question: `Can I afford to live in ${city.name} on ${formatMoney(salary)}?`, answer: `Our verdict: ${result.verdict}. After all taxes and essential expenses (rent, groceries, transportation, utilities, healthcare), you'd have ${formatMoney(result.remaining_monthly)} remaining each month. Your rent-to-income ratio would be ${result.rent_to_income_ratio}%.` },
    { question: `What percentage of my income goes to rent in ${city.name}?`, answer: `Rent for a 1-bedroom apartment (${formatMoney(result.rent_1br)}/mo) would consume ${result.rent_to_income_ratio}% of your monthly take-home pay. Financial experts recommend keeping this under 30%.` },
    { question: `Is ${formatMoney(salary)} a good salary in ${city.name}?`, answer: `The median household income in ${city.name} is ${formatMoney(city.median_household_income)}. A ${formatMoney(salary)} salary is ${salary > city.median_household_income ? 'above' : 'below'} the median, which ${salary > city.median_household_income ? 'gives you an advantage' : 'may make budgeting more important'}.` },
  ];

  return (
    <div data-testid="salary-in-city-page">
      <script type="application/ld+json">{JSON.stringify(generateFAQSchema(faqs))}</script>
      <script type="application/ld+json">{JSON.stringify(generateBreadcrumbSchema([
        { name: "Home", url: "/" }, { name: city.name, url: `/cost-of-living-in-${city.slug}` },
        { name: `${formatMoney(salary)} Salary` }
      ]))}</script>
      <script type="application/ld+json">{JSON.stringify(generateSpeakableSchema(`/${salary/1000}k-salary-in-${citySlug}`, [`${formatMoney(salary)} Salary in ${city.name}`], [`On a ${formatMoney(salary)} salary in ${city.name}, your monthly take-home is approximately ${formatMoney(result.net_monthly)}. Verdict: ${result.verdict}.`]))}</script>

      <div className="bg-slate-50 border-b border-slate-100 py-3">
        <div className="container-main flex items-center gap-2 text-xs text-slate-500">
          <Link to="/" className="hover:text-slate-700 no-underline">Home</Link><span>/</span>
          <Link to={`/cost-of-living-in-${city.slug}`} className="hover:text-slate-700 no-underline">{city.name}</Link><span>/</span>
          <span className="text-slate-700">{formatMoney(salary)} Salary</span>
        </div>
      </div>

      <section className="hero-pattern py-12 md:py-16">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4">
            {isTaxPage ? `${formatMoney(salary)} After Tax in ${city.state}` : `${formatMoney(salary)} Salary in ${city.name}`}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className={`px-4 py-2 rounded-full text-sm font-bold ${verdictClass}`} data-testid="salary-verdict">{result.verdict}</span>
            <Badge variant="secondary">{city.name}, {city.state_code}</Badge>
          </div>

          <Card className="shadow-lg">
            <CardContent className="p-6 md:p-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">Monthly Take-Home</p>
                  <p className="text-xl font-bold font-mono text-emerald-700">{formatMoney(result.net_monthly)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">Monthly Expenses</p>
                  <p className="text-xl font-bold font-mono text-slate-700">{formatMoney(result.total_monthly_expenses)}</p>
                </div>
                <div className={`rounded-xl p-4 text-center col-span-2 md:col-span-1 ${result.remaining_monthly >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <p className="text-xs text-slate-500 mb-1">Remaining</p>
                  <p className={`text-xl font-bold font-mono ${result.remaining_monthly >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    {formatMoney(result.remaining_monthly)}
                  </p>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-500" /> Tax Breakdown (Annual)
              </h3>
              <div className="space-y-2 mb-6">
                {[
                  { label: "Federal Tax", value: result.federal_tax },
                  { label: "State Tax", value: result.state_tax },
                  { label: "FICA (SS + Medicare)", value: result.fica_tax },
                ].map((t, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">{t.label}</span>
                    <span className="font-mono text-slate-800">{formatMoney(t.value)}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center text-sm font-semibold border-t border-slate-200 pt-2">
                  <span className="text-slate-900">Total Tax</span>
                  <span className="font-mono text-red-600">{formatMoney(result.total_tax)}</span>
                </div>
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span className="text-slate-900">Effective Tax Rate</span>
                  <span className="font-mono text-slate-700">{((result.total_tax / salary) * 100).toFixed(1)}%</span>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" /> Monthly Expenses
              </h3>
              {[
                { label: "Rent (1BR)", value: result.rent_1br },
                { label: "Groceries", value: result.groceries },
                { label: "Transportation", value: result.transportation },
                { label: "Utilities", value: result.utilities },
                { label: "Healthcare", value: result.healthcare },
              ].map((e, i) => (
                <div key={i} className="mb-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">{e.label}</span>
                    <span className="font-mono text-slate-700">{formatMoney(e.value)}</span>
                  </div>
                  <Progress value={Math.min((e.value / result.net_monthly) * 100, 100)} className="h-2" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

            {/* AFFILIATE SIDEBAR - Money maker */}
            <div className="hidden lg:block">
              <SponsorSidebar pageType="salary" cityName={city.name} />
            </div>
          </div>
        </div>
      </section>

      {/* Inline affiliate banner */}
      <div className="container-main">
        <InlineBanner cityName={city.name} />

        {/* Post-Result Housing Banner */}
        {/* Apartment Affiliate Banner */}
        <a href="https://www.hellolanding.com/r/web/2vvg1H" target="_blank" rel="noopener" className="no-underline block">
          <div className="p-4 bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 border border-blue-200 rounded-xl mb-6 flex items-center gap-4 hover:shadow-md transition-all" data-testid="post-result-banner">
            <img src="https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop" alt="Furnished apartment" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
            <div className="flex-1">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Sponsored</p>
              <p className="text-sm font-semibold text-slate-900">Moving to {city.name}? Get $250 Off Your First Furnished Apartment</p>
              <p className="text-xs text-slate-500">Move freely, stay comfortably. Furnished apartments in 200+ cities. *Limited time offer. Terms apply.</p>
            </div>
            <span className="text-xs font-bold text-blue-600 whitespace-nowrap hidden md:inline">Claim $250 Off &rarr;</span>
          </div>
        </a>
      </div>

      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container-main max-w-3xl">
          <FinancialDisclaimer />
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-4 bg-white">
                <AccordionTrigger className="text-sm font-medium text-slate-800 hover:no-underline py-4">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 pb-4">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-12">
        <div className="container-main text-center">
          <Link to="/">
            <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="back-to-calculator">
              <DollarSign className="w-4 h-4 mr-2" /> Try Different Salary
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
